import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../DashboardLayout';
import { Button } from '../../ui';
import { TrendingUpIcon, DollarSignIcon, UsersIcon, SparklesIcon, UploadCloudIcon, SearchIcon, FilterIcon, ArrowRightIcon, ArrowUpRightIcon, StarIcon, RepeatIcon, TargetIcon, ZapIcon, ChevronRightIcon, BuildingIcon } from 'lucide-react';
type Client = {
  id: string;
  company: string;
  contact: string;
  industry: string;
  lastBooking: string;
  totalRevenue: number;
  ltv: number;
  bookings: number;
  upsellScore: number;
  upsellPotential: number;
  status: 'hot' | 'warm' | 'cold';
  signal: string;
};
const CLIENTS: Client[] = [{
  id: 'c1',
  company: 'Northwind Capital',
  contact: 'Erin Park',
  industry: 'Finance',
  lastBooking: '2 mo ago',
  totalRevenue: 184000,
  ltv: 412000,
  bookings: 4,
  upsellScore: 94,
  upsellPotential: 68000,
  status: 'hot',
  signal: 'Recurring annual offsite — never bought experience layer'
}, {
  id: 'c2',
  company: 'Atlas Health Group',
  contact: 'Marcus Lee',
  industry: 'Healthcare',
  lastBooking: '6 weeks ago',
  totalRevenue: 312000,
  ltv: 540000,
  bookings: 7,
  upsellScore: 92,
  upsellPotential: 84000,
  status: 'hot',
  signal: 'Booked 3 leadership retreats — asked about wellness add-ons'
}, {
  id: 'c3',
  company: 'Kestrel Studios',
  contact: 'Priya Shah',
  industry: 'Creative',
  lastBooking: '3 weeks ago',
  totalRevenue: 96000,
  ltv: 220000,
  bookings: 3,
  upsellScore: 88,
  upsellPotential: 42000,
  status: 'hot',
  signal: 'Growing team — moved from boardroom to resort venues'
}, {
  id: 'c4',
  company: 'Meridian Logistics',
  contact: 'Sam Okafor',
  industry: 'Logistics',
  lastBooking: '4 mo ago',
  totalRevenue: 240000,
  ltv: 320000,
  bookings: 5,
  upsellScore: 76,
  upsellPotential: 38000,
  status: 'warm',
  signal: 'Stable rebooking pattern — learning theme requested'
}, {
  id: 'c5',
  company: 'Brightline Tech',
  contact: 'Dana Rivera',
  industry: 'SaaS',
  lastBooking: '8 weeks ago',
  totalRevenue: 158000,
  ltv: 290000,
  bookings: 4,
  upsellScore: 71,
  upsellPotential: 32000,
  status: 'warm',
  signal: 'Sales kickoff annually — engagement low last event'
}, {
  id: 'c6',
  company: 'Harbor & Hill Law',
  contact: 'Tom Beaumont',
  industry: 'Legal',
  lastBooking: '5 mo ago',
  totalRevenue: 88000,
  ltv: 165000,
  bookings: 2,
  upsellScore: 64,
  upsellPotential: 18000,
  status: 'warm',
  signal: 'Partner retreat — formal style, learning fits'
}, {
  id: 'c7',
  company: 'Solace Wellness Co.',
  contact: 'Lina Chen',
  industry: 'Wellness',
  lastBooking: '1 yr ago',
  totalRevenue: 52000,
  ltv: 92000,
  bookings: 2,
  upsellScore: 48,
  upsellPotential: 14000,
  status: 'cold',
  signal: 'Lapsed — re-engagement window'
}, {
  id: 'c8',
  company: 'Forge Manufacturing',
  contact: 'Rick Donovan',
  industry: 'Industrial',
  lastBooking: '14 mo ago',
  totalRevenue: 124000,
  ltv: 180000,
  bookings: 3,
  upsellScore: 38,
  upsellPotential: 12000,
  status: 'cold',
  signal: 'Lapsed — limited fit, lower priority'
}];
const fmt = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${n}`;
export function ClientBookPage() {
  const [filter, setFilter] = useState<'all' | 'hot' | 'warm' | 'cold'>('all');
  const [query, setQuery] = useState('');
  const filtered = CLIENTS.filter(c => (filter === 'all' || c.status === filter) && (c.company.toLowerCase().includes(query.toLowerCase()) || c.contact.toLowerCase().includes(query.toLowerCase())));
  const totalBookValue = CLIENTS.reduce((s, c) => s + c.totalRevenue, 0);
  const totalLTV = CLIENTS.reduce((s, c) => s + c.ltv, 0);
  const totalUpsell = CLIENTS.reduce((s, c) => s + c.upsellPotential, 0);
  const hotCount = CLIENTS.filter(c => c.status === 'hot').length;
  const statusStyle: Record<Client['status'], string> = {
    hot: 'bg-brand-coral/10 text-brand-coral',
    warm: 'bg-amber-50 text-amber-700',
    cold: 'bg-slate-100 text-slate-500'
  };
  return <DashboardLayout pageTitle="Client Book" breadcrumb="Revenue Intelligence" data-id="element-774">
      {/* Hero / Revenue summary */}
      <div className="bg-gradient-to-br from-brand-navy via-slate-900 to-brand-navy rounded-2xl shadow-soft p-8 text-white relative overflow-hidden mb-8" data-id="element-775">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-teal/15 rounded-full blur-3xl" data-id="element-776" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-brand-coral/10 rounded-full blur-3xl" data-id="element-777" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8" data-id="element-778">
          <div data-id="element-779">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/10 rounded-full px-3 py-1 text-xs font-bold text-teal-300 uppercase tracking-wider mb-4" data-id="element-780">
              <SparklesIcon className="w-3.5 h-3.5" data-id="element-781" /> AI Revenue Intelligence
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-782">
              Your book is worth{' '}
              <span className="text-brand-coral" data-id="element-783">
                ${(totalLTV / 1000).toFixed(0)}K
              </span>{' '}
              in lifetime value.
            </h2>
            <p className="text-slate-300 max-w-2xl" data-id="element-784">
              {hotCount} hot upsell opportunities identified across your active
              clients. Estimated incremental revenue from Offsite+ experience
              layer:{' '}
              <span className="font-bold text-white" data-id="element-785">
                ${(totalUpsell / 1000).toFixed(0)}K
              </span>
              .
            </p>
          </div>
          <div className="flex gap-3" data-id="element-786">
            <Link to="/dashboard/clients/upload" data-id="element-787">
              <Button variant="accent" data-id="element-788">
                <UploadCloudIcon className="w-4 h-4 mr-2" data-id="element-789" /> Import Client Book
              </Button>
            </Link>
            <Button variant="outline" className="text-white border-white/20 hover:bg-white/10" data-id="element-790">
              <SparklesIcon className="w-4 h-4 mr-2" data-id="element-791" /> Re-run AI Analysis
            </Button>
          </div>
        </div>

        <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-792">
          <RevenueStat icon={<DollarSignIcon className="w-4 h-4" data-id="element-794" />} label="Total Book Revenue" value={fmt(totalBookValue)} sub="Last 24 months" data-id="element-793" />
          <RevenueStat icon={<RepeatIcon className="w-4 h-4" data-id="element-796" />} label="Lifetime Value" value={fmt(totalLTV)} sub="Projected, all clients" highlight data-id="element-795" />
          <RevenueStat icon={<ZapIcon className="w-4 h-4" data-id="element-798" />} label="Upsell Pipeline" value={fmt(totalUpsell)} sub={`${hotCount} hot · ${CLIENTS.filter(c => c.status === 'warm').length} warm`} data-id="element-797" />
          <RevenueStat icon={<UsersIcon className="w-4 h-4" data-id="element-800" />} label="Total Clients" value={String(CLIENTS.length)} sub="Imported & analyzed" data-id="element-799" />
        </div>
      </div>

      {/* AI Insight band */}
      <div className="grid lg:grid-cols-3 gap-4 mb-8" data-id="element-801">
        <InsightCard tone="coral" icon={<TargetIcon className="w-5 h-5" data-id="element-803" />} title="3 clients are likely buyers this quarter" body="Atlas Health, Northwind Capital, and Kestrel Studios have rebooking patterns and stated goals that match Offsite+ premium tier. Combined upsell potential: $194K." cta="Review hot list" data-id="element-802" />
        <InsightCard tone="teal" icon={<TrendingUpIcon className="w-5 h-5" data-id="element-805" />} title="Average LTV grew 22% YoY" body="Clients who used a structured experience layer extended their booking cadence from 1.4 to 2.1 events/yr. Apply this signal to your pipeline." cta="See LTV trends" data-id="element-804" />
        <InsightCard tone="navy" icon={<StarIcon className="w-5 h-5" data-id="element-807" />} title="2 lapsed clients are reactivation targets" body="Solace Wellness and Forge Mfg. last booked 12+ months ago. AI suggests a re-engagement proposal with a wellness-anchored package." cta="Generate proposal" data-id="element-806" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4" data-id="element-808">
        <div className="flex bg-slate-100 p-1 rounded-lg" data-id="element-809">
          {(['all', 'hot', 'warm', 'cold'] as const).map(f => <button key={f} onClick={() => setFilter(f)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors capitalize ${filter === f ? 'bg-white text-brand-navy shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-810">
              {f === 'all' ? 'All clients' : f}
              <span className="ml-1.5 text-xs text-slate-400" data-id="element-811">
                {f === 'all' ? CLIENTS.length : CLIENTS.filter(c => c.status === f).length}
              </span>
            </button>)}
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto" data-id="element-812">
          <div className="relative flex-1 md:w-72" data-id="element-813">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-814" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search clients…" className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal" data-id="element-815" />
          </div>
          <Button variant="outline" size="sm" className="px-3" data-id="element-816">
            <FilterIcon className="w-4 h-4" data-id="element-817" />
          </Button>
        </div>
      </div>

      {/* Client table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-818">
        <div className="overflow-x-auto" data-id="element-819">
          <table className="w-full text-left text-sm" data-id="element-820">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-medium" data-id="element-821">
              <tr data-id="element-822">
                <th className="px-6 py-4" data-id="element-823">Client</th>
                <th className="px-6 py-4" data-id="element-824">Industry</th>
                <th className="px-6 py-4" data-id="element-825">Bookings</th>
                <th className="px-6 py-4" data-id="element-826">Revenue · LTV</th>
                <th className="px-6 py-4" data-id="element-827">Upsell Score</th>
                <th className="px-6 py-4" data-id="element-828">Potential</th>
                <th className="px-6 py-4 text-right" data-id="element-829">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-830">
              {filtered.map((c, i) => <motion.tr key={c.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.03
            }} className="hover:bg-slate-50/50 transition-colors" data-id="element-831">
                  <td className="px-6 py-4" data-id="element-832">
                    <div className="flex items-center gap-3" data-id="element-833">
                      <div className="w-9 h-9 rounded-lg bg-brand-navy/5 text-brand-navy flex items-center justify-center" data-id="element-834">
                        <BuildingIcon className="w-4 h-4" data-id="element-835" />
                      </div>
                      <div data-id="element-836">
                        <div className="font-bold text-slate-900" data-id="element-837">
                          {c.company}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-838">
                          {c.contact} · last booked {c.lastBooking}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600" data-id="element-839">{c.industry}</td>
                  <td className="px-6 py-4 text-slate-700 font-medium" data-id="element-840">
                    {c.bookings}
                  </td>
                  <td className="px-6 py-4" data-id="element-841">
                    <div className="font-bold text-brand-navy" data-id="element-842">
                      {fmt(c.totalRevenue)}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-843">
                      LTV {fmt(c.ltv)}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-844">
                    <div className="flex items-center gap-3" data-id="element-845">
                      <div className="w-20 bg-slate-100 rounded-full h-1.5 overflow-hidden" data-id="element-846">
                        <div className={`h-1.5 rounded-full ${c.upsellScore >= 80 ? 'bg-brand-coral' : c.upsellScore >= 60 ? 'bg-amber-400' : 'bg-slate-300'}`} style={{
                      width: `${c.upsellScore}%`
                    }} data-id="element-847" />
                      </div>
                      <span className="text-sm font-bold text-brand-navy w-8" data-id="element-848">
                        {c.upsellScore}
                      </span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusStyle[c.status]}`} data-id="element-849">
                        {c.status}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1.5 max-w-md truncate" data-id="element-850">
                      {c.signal}
                    </div>
                  </td>
                  <td className="px-6 py-4 font-bold text-brand-teal" data-id="element-851">
                    +{fmt(c.upsellPotential)}
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-852">
                    <Link to={`/dashboard/clients/${c.id}`} className="inline-flex items-center gap-1 text-brand-navy text-sm font-medium hover:text-brand-teal transition-colors" data-id="element-853">
                      Review <ChevronRightIcon className="w-4 h-4" data-id="element-854" />
                    </Link>
                  </td>
                </motion.tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>;
}
function RevenueStat({
  icon,
  label,
  value,
  sub,
  highlight
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  highlight?: boolean;
}) {
  return <div className={`rounded-xl p-4 border ${highlight ? 'bg-brand-coral/10 border-brand-coral/30' : 'bg-white/5 border-white/10'}`} data-id="element-855">
      <div className="flex items-center gap-2 text-xs text-slate-300 font-medium mb-2" data-id="element-856">
        <span className={highlight ? 'text-brand-coral' : 'text-teal-300'} data-id="element-857">
          {icon}
        </span>
        {label}
      </div>
      <div className="text-2xl font-black mb-1" data-id="element-858">{value}</div>
      <div className="text-[11px] text-slate-400" data-id="element-859">{sub}</div>
    </div>;
}
function InsightCard({
  tone,
  icon,
  title,
  body,
  cta
}: {
  tone: 'coral' | 'teal' | 'navy';
  icon: React.ReactNode;
  title: string;
  body: string;
  cta: string;
}) {
  const styles = {
    coral: {
      bg: 'bg-brand-coral/5 border-brand-coral/20',
      iconBg: 'bg-brand-coral text-white',
      cta: 'text-brand-coral'
    },
    teal: {
      bg: 'bg-brand-teal/5 border-brand-teal/20',
      iconBg: 'bg-brand-teal text-white',
      cta: 'text-brand-teal'
    },
    navy: {
      bg: 'bg-brand-navy/5 border-brand-navy/15',
      iconBg: 'bg-brand-navy text-white',
      cta: 'text-brand-navy'
    }
  }[tone];
  return <div className={`rounded-2xl border p-5 ${styles.bg} flex flex-col`} data-id="element-860">
      <div className="flex items-start gap-3 mb-3" data-id="element-861">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${styles.iconBg}`} data-id="element-862">
          {icon}
        </div>
        <h3 className="font-bold text-brand-navy leading-tight pt-1" data-id="element-863">
          {title}
        </h3>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed mb-4 flex-1" data-id="element-864">
        {body}
      </p>
      <button className={`text-sm font-bold flex items-center gap-1 ${styles.cta} hover:gap-2 transition-all w-fit`} data-id="element-865">
        {cta} <ArrowUpRightIcon className="w-4 h-4" data-id="element-866" />
      </button>
    </div>;
}